import {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from "react";

import { useScript, type UseScriptStatus } from "common/core/hooks/use-script";
import Env from "config/environment";

export type GoogleMapsAPI = typeof google.maps;
export type GoogleMapsEventListener = google.maps.MapsEventListener;
export type PlaceResult = google.maps.places.PlaceResult;

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    initGoogleMaps?: () => void;
  }
}

const GOOGLE_MAPS_URL = "https://maps.googleapis.com/maps/api/js";

type GoogleMapsContextType = {
  isLoaded: boolean;
  googleMaps: GoogleMapsAPI | null;
  setShouldLoad: Dispatch<SetStateAction<boolean>>;
};

const GoogleMapsContext = createContext<GoogleMapsContextType | null>(null);

export const useGoogleMaps = (): Omit<GoogleMapsContextType, "setShouldLoad"> => {
  const context = useContext(GoogleMapsContext);

  if (context === null) {
    throw new Error("useGoogleMaps must be used within a GoogleMapsProvider");
  }

  const { googleMaps, isLoaded, setShouldLoad } = context;

  useEffect(() => {
    if (!isLoaded) {
      setShouldLoad(true);
    }
  }, [isLoaded, setShouldLoad]);

  return { isLoaded, googleMaps };
};

type GoogleMapsProviderProps = {
  children: ReactNode;
};

export const GoogleMapsProvider = ({ children }: GoogleMapsProviderProps) => {
  const [shouldLoad, setShouldLoad] = useState<boolean>(false);
  const [googleMaps, setGoogleMaps] = useState<GoogleMapsAPI | null>(null);

  useEffect(() => {
    window.initGoogleMaps = () => {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (window.google.maps) {
        setGoogleMaps(window.google.maps);
      }
    };

    return () => {
      delete window.initGoogleMaps;
    };
  }, []);

  const status: UseScriptStatus = useScript(GOOGLE_MAPS_URL, {
    shouldPreventLoad: !shouldLoad,
    id: "google-maps-script",
    urlParams: {
      key: Env.googleMapsApiKey,
      libraries: "places",
      callback: "initGoogleMaps",
      loading: "async",
    },
  });

  const isLoaded = status === "ready" && googleMaps !== null;

  const contextValue = useMemo<GoogleMapsContextType>(
    () => ({
      isLoaded,
      googleMaps,
      setShouldLoad,
    }),
    [isLoaded, googleMaps, setShouldLoad],
  );

  return <GoogleMapsContext.Provider value={contextValue}>{children}</GoogleMapsContext.Provider>;
};
